import { useState } from "react";
import { api } from "../../lib/api";
import { st } from "../../styles.js";
import { Icon, TI } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Glyph } from "../../components/ui";
import { Button, IconButton } from "../../components/ui/ds";
import { todayKey, ymd, WEEKDAYS_SK } from "../../lib/format";
import { remindersForMonth, buildMonthCells } from "../../lib/calendar";
import { REPEAT_SHORT } from "../../lib/constants";
import { PlanItemRow } from "./PlanItemRow";

export function CalendarTab({ s, reload, items, undatedItems = [], onAddForDay, onOpenDetail }) {
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [sel, setSel] = useState(todayKey());

  const byDay = remindersForMonth(items || s.reminders, view.y, view.m);
  const cells = buildMonthCells(view.y, view.m);  // v26.3 §1 — helper (zdieľané s DateField)

  const monthName = new Date(view.y, view.m, 1)
    .toLocaleDateString("sk-SK", { month: "long", year: "numeric" });
  const tKey = todayKey();

  function shift(delta) {
    setView((p) => {
      const d = new Date(p.y, p.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  }

  const selList = byDay[sel] || [];
  const selPretty = new Date(sel + "T00:00:00")
    .toLocaleDateString("sk-SK", { weekday: "long", day: "numeric", month: "long" });
  async function done(id) { await api("/api/tasks/" + id + "/done", { method: "POST" }); reload(); }
  async function undo(id) { await api("/api/tasks/" + id + "/undo", { method: "POST" }); reload(); }

  return (
    <>
      <div style={st.calHead}>
        <IconButton variant="surface" size="sm" aria-label="Predchádzajúci mesiac" title="Predchádzajúci mesiac" onClick={() => shift(-1)}><Icon name="chevron" size={15} color="currentColor" style={{ transform: "rotate(180deg)" }} /></IconButton>
        <span style={st.calTitle}>{monthName}</span>
        <IconButton variant="surface" size="sm" aria-label="Nasledujúci mesiac" title="Nasledujúci mesiac" onClick={() => shift(1)}><Icon name="chevron" size={15} color="currentColor" /></IconButton>
      </div>

      <div style={st.calGrid}>
        {WEEKDAYS_SK.map((w) => <div key={w} style={st.calDow}>{w}</div>)}
        {cells.map((d, i) => {
          if (d === null) return <div key={"e" + i} style={st.calEmpty} />;
          const key = ymd(view.y, view.m, d);
          const items = byDay[key] || [];
          const isToday = key === tKey;
          const isSel = key === sel;
          return (
            <button key={key} onClick={() => { if (isSel && onAddForDay) onAddForDay(key, "event"); else setSel(key); }}
              style={{ ...st.calCell, ...(items.length ? st.calCellHas : {}),
                ...(isToday ? st.calCellToday : {}), ...(isSel ? st.calCellSel : {}) }}>
              <span style={{ ...st.calDayNum, ...(isToday ? st.calDayNumToday : {}) }}>{d}</span>
              <span style={st.calIcons}>
                {items.slice(0, 3).map((r, j) => (
                  <span key={j} style={{ ...st.calIcon, ...(r.done ? st.calIconDone : {}) }}><Glyph icon={r.icon} fallback="bell" size={13} /></span>
                ))}
                {items.length > 3 && <span style={st.calMore}>+{items.length - 3}</span>}
              </span>
            </button>
          );
        })}
      </div>

      <div style={{ ...st.progressHead, marginTop: 20 }}>
        <span className="bz-subhead"><TI name="calendar" color="var(--info)" />{selPretty}</span>
        <span style={st.progressPct}>{selList.length}{" "}
          {selList.length === 1 ? "položka" : (selList.length >= 2 && selList.length <= 4 ? "položky" : "položiek")}</span>
      </div>
      {/* v25 §7.5 — zoznam dňa cez zdieľaný PlanItemRow (rovnaké riadky ako Zoznam) */}
      {selList.length > 0 && (
        <div className="bz-list">
          {selList.map((r, i) => (
            <PlanItemRow key={r.id + "-" + i} item={r} event={r.plan_type === "event"} showBorder={i > 0}
              recentlyDone={!!r.done}
              whenNode={<>{r.time ? `${r.time}${r.endTime ? "–" + r.endTime : ""}` : "celý deň"}{r.repeat !== "none" ? " · " + (REPEAT_SHORT[r.repeat] || "") : ""}</>}
              onOpen={onOpenDetail ? () => onOpenDetail(r) : undefined}
              onDone={!r._shared && r.plan_type !== "event" && !r.done ? () => done(r.id) : undefined}
              onUndo={!r._shared && r.done ? () => undo(r.id) : undefined} />
          ))}
        </div>
      )}
      {selList.length === 0 && <div style={st.empty}><BuzerIcon name="leaf" size={18} color="var(--muted)" style={{ verticalAlign: "-3px", marginRight: 6 }} />V tento deň nič</div>}
      {/* v16 §9 — quick-create on the tapped day (defaults to Pripomienka; switch to Udalosť in the form) */}
      {onAddForDay && <Button variant="outline" block style={{ marginTop: 10 }} onClick={() => onAddForDay(sel)}><BuzerIcon name="add" size={16} color="currentColor" strokeWidth={2.2} />Pridať na tento deň</Button>}

      {/* Bez termínu — všetky tasky/poznámky bez dátumu (nezmestia sa do kalendárovej mriežky). */}
      {undatedItems.length > 0 && (
        <>
          <div style={{ ...st.progressHead, marginTop: 24 }}>
            <span className="bz-subhead"><TI name="clipboard" color="var(--muted)" />Bez termínu</span>
            <span style={st.progressPct}>{undatedItems.length}{" "}
              {undatedItems.length === 1 ? "položka" : (undatedItems.length >= 2 && undatedItems.length <= 4 ? "položky" : "položiek")}</span>
          </div>
          <div className="bz-list">
            {undatedItems.map((r, i) => (
              <PlanItemRow key={r.id + "-u" + i} item={r} event={r.plan_type === "event"} showBorder={i > 0}
                recentlyDone={!!r.done}
                onOpen={onOpenDetail ? () => onOpenDetail(r) : undefined}
                onDone={!r._shared && r.plan_type !== "event" && !r.done ? () => done(r.id) : undefined}
                onUndo={!r._shared && r.done ? () => undo(r.id) : undefined} />
            ))}
          </div>
        </>
      )}
    </>
  );
}
